import type { ImageStore, PromoWithFormat, Translations } from '@ps-int/promozavr/app/api/types';

export const PROMOZAVR_PROJECT = 'telemostDesktop';
export const PROMOZAVR_PERSISTENT_PROJECT = 'telemostPersistentDesktop';
export const PROMOZAVR_TOUCH_PROJECT = 'telemostTouch';
export const PROMOZAVR_PERSISTENT_TOUCH_PROJECT = 'telemostPersistentTouch';
export const PROMOZAVR_STAGE = 'page-init';
export const PROMOZAVR_MODEL = 'promozavr/select-promo';
export const PROMOZAVR_SELECT_MULTIPLE_PROMOS_MODEL = 'promozavr/select-multiple-promos';
export const FETCH_YOURSELF = 'fetch_yourself';
export const PROMOZAVR_TEST_APP = 'test_project';
export const PROMOZAVR_TEST_BUNKER_VERSION = 'test';
export const PROMOZAVR_REGULAR_QUEUE = 'regular';
export const PROMOZAVR_PERSISTENT_QUEUE = 'persistent';
export const PROMOZAVR_QUEUE_KINDS = [PROMOZAVR_REGULAR_QUEUE, PROMOZAVR_PERSISTENT_QUEUE] as const;
export const PROMOZAVR_PERSISTENT_SKIP_CHECKERS = ['showFrequency'] as const;
export const PROMOZAVR_DESKTOP_CHECKER_COLLECTION_ID = 'desktop';
export const PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID = 'mobile';
export const PROMOZAVR_CHECKER_COLLECTION_IDS = [
    PROMOZAVR_DESKTOP_CHECKER_COLLECTION_ID,
    PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID,
] as const;

export type PromozavrRendererSource = 'local' | 'remote';
export type PromozavrStatus = 'idle' | 'loading' | 'success' | 'error';
export type PromozavrAllowedApp = typeof PROMOZAVR_TEST_APP;
export type PromozavrBunkerVersion = 'test' | 'prod';
export type PromozavrQueueKind = (typeof PROMOZAVR_QUEUE_KINDS)[number];
export type PromozavrSkipChecker = (typeof PROMOZAVR_PERSISTENT_SKIP_CHECKERS)[number];
export type PromozavrCheckerCollectionId = (typeof PROMOZAVR_CHECKER_COLLECTION_IDS)[number];
export type PromozavrRegularProject = typeof PROMOZAVR_PROJECT | typeof PROMOZAVR_TOUCH_PROJECT;
export type PromozavrPersistentProject =
    | typeof PROMOZAVR_PERSISTENT_PROJECT
    | typeof PROMOZAVR_PERSISTENT_TOUCH_PROJECT;
export type PromozavrProject = PromozavrRegularProject | PromozavrPersistentProject;

export type SelectPromoSuppliersData = {
    bunkerConfigSupplierData: typeof FETCH_YOURSELF;
    billingDataSupplierData: typeof FETCH_YOURSELF;
    billingSubscriptionsDataSupplierData: typeof FETCH_YOURSELF;
    blackboxUserInfoSupplierData: typeof FETCH_YOURSELF;
    datasyncPromoDataSupplierData: typeof FETCH_YOURSELF;
    datasyncUserFeaturesDataSupplierData: typeof FETCH_YOURSELF;
    saasKvSegmentsSupplierData: typeof FETCH_YOURSELF;
};

export type PromozavrQueryParams = {
    app?: PromozavrAllowedApp;
    debug?: string;
    debugParams?: string;
};

export type SelectPromoModelEntryBase = {
    stage: typeof PROMOZAVR_STAGE;
    lang?: string;
    tld?: string;
    testIds: string[];
    checkerCollectionId?: PromozavrCheckerCollectionId;
    debugParams?: string;
    skipPromos: string[];
    selectAllPromos: false;
};

export type SelectPromoModelDesktopRegularEntry = SelectPromoModelEntryBase & {
    project: typeof PROMOZAVR_PROJECT;
    usePersistentQueues: false;
    checkerCollectionId?: never;
};

export type SelectPromoModelTouchRegularEntry = SelectPromoModelEntryBase & {
    project: typeof PROMOZAVR_TOUCH_PROJECT;
    usePersistentQueues: false;
    checkerCollectionId: typeof PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID;
};

export type SelectPromoModelRegularEntry = SelectPromoModelDesktopRegularEntry | SelectPromoModelTouchRegularEntry;

export type SelectPromoModelDesktopPersistentEntry = SelectPromoModelEntryBase & {
    project: typeof PROMOZAVR_PERSISTENT_PROJECT;
    usePersistentQueues: true;
    checkerCollectionId?: never;
    skipCheckers: PromozavrSkipChecker[];
};

export type SelectPromoModelTouchPersistentEntry = SelectPromoModelEntryBase & {
    project: typeof PROMOZAVR_PERSISTENT_TOUCH_PROJECT;
    usePersistentQueues: true;
    checkerCollectionId: typeof PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID;
    skipCheckers: PromozavrSkipChecker[];
};

export type SelectPromoModelPersistentEntry =
    | SelectPromoModelDesktopPersistentEntry
    | SelectPromoModelTouchPersistentEntry;

export type SelectPromoModelEntry = SelectPromoModelRegularEntry | SelectPromoModelPersistentEntry;

export type SelectPromoModelInput = PromozavrQueryParams &
    SelectPromoModelDesktopRegularEntry & {
        suppliersData: SelectPromoSuppliersData;
    };

export type SelectMultiplePromosModelEntries = {
    [PROMOZAVR_REGULAR_QUEUE]: SelectPromoModelRegularEntry;
    [PROMOZAVR_PERSISTENT_QUEUE]: SelectPromoModelPersistentEntry;
};

export type SelectMultiplePromosModelInput = Pick<PromozavrQueryParams, 'app' | 'debug'> & {
    entries: SelectMultiplePromosModelEntries;
    suppliersData: SelectPromoSuppliersData;
};

export type SelectPromoModelInternalInput = SelectPromoModelInput & {
    bunkerVersion?: PromozavrBunkerVersion;
};

export type SelectPromoModelServerInput = SelectPromoModelInternalInput & {
    fastFail?: boolean;
};

export type SelectMultiplePromosModelInternalInput = SelectMultiplePromosModelInput & {
    bunkerVersion?: PromozavrBunkerVersion;
};

export type SelectMultiplePromosModelServerInput = SelectMultiplePromosModelInternalInput & {
    fastFail?: boolean;
};

export type SelectPromoModelResponse = {
    selectedPromos?: PromoWithFormat[];
    translations?: Translations;
    images?: ImageStore;
    isSelected: boolean;
};

export type SelectMultiplePromosModelResponse = Record<PromozavrQueueKind, SelectPromoModelResponse>;

export type PromozavrQueueSelection = {
    selectedPromo?: PromoWithFormat;
    translations?: Translations;
    images?: ImageStore;
    isSelected: boolean;
};

export type PromozavrQueueSelections = Record<PromozavrQueueKind, PromozavrQueueSelection>;

export type PromozavrState = {
    status: PromozavrStatus;
    request?: SelectMultiplePromosModelServerInput;
    requestKey?: string;
    queueSelections: PromozavrQueueSelections;
    rendererSource: PromozavrRendererSource;
    lastError?: string;
};
